import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateLeague, UpsertLeaguePayload } from "../types/upsertLeague.ts";
import { useCreateLeague } from "./useCreateLeague.ts";
import { useDeleteLeague } from "./useDeleteLeague.ts";
import { useFetchLeague } from "./useFetchLeague.ts";
import { useUpdateLeague } from "./useUpdateLeague.ts";

export const useLeagueService = () => {
  const { data: league, isLoading: isFetchLoading } = useFetchLeague();
  const { mutateAsync: createLeagueFn, isPending: isCreatePending } = useCreateLeague();
  const { mutateAsync: updateLeagueFn, isPending: isUpdatePending } = useUpdateLeague();
  const { mutateAsync: deleteLeagueFn, isPending: isDeletePending } = useDeleteLeague();

  const { create, update, remove } = useApiOperations(ApiOperationItems.LEAGUE);

  const upsertLeague = async (upsertModel: UpsertLeaguePayload) => {
    const { leagueId, ...body } = upsertModel;

    if (leagueId) {
      return update(() => updateLeagueFn({ ...body, leagueId }), { successMessageId: "league.update.success" });
    }

    return create(() => createLeagueFn(body as CreateLeague), {
      successMessageId: "league.create.success",
    });
  };

  const deleteLeague = async (leagueId: string) =>
    Boolean(await remove(() => deleteLeagueFn(leagueId), { successMessageId: "league.delete.success" }));

  const isUpsertPending = isCreatePending || isUpdatePending;

  return {
    league,
    isFetchLoading,
    upsertLeague,
    isUpsertPending,
    deleteLeague,
    isDeletePending,
  };
};
